import type {
    CardOrderDetailsType,
    CompanyDetailsType,
    ContactDetailsType,
    ContractUpdateType,
    OnboardingRequestType,
    ShippingAddressType,
} from '@/api/schema';
import type { ChargingCardsSchemaType } from '@/components/steps/ChargingCardsStep';
import type { InvoiceDataSchemaType } from '@/components/steps/InvoicingStep/invoiceDataSchema';
import type { ShippingSchemaType } from '@/components/steps/ShippingAddressStep/ShippingAddressStep';
import { ADDRESS_TYPE } from '@/components/steps/ShippingAddressStep/shippingSchema';
import type { ContactDetailsSchemaType } from '@/features/administration/contract/validations/contact';
import type { InvoiceDetailsSchemaType } from '@/features/administration/contract/validations/invoice';
import { buildPhoneNumber } from '@/lib/utils';

const buildShippingAddress = (
    shippingAddressState: ShippingSchemaType,
    isShippingAddressRedesignEnabled: boolean
): ShippingAddressType => ({
    isCompanyAddress: isShippingAddressRedesignEnabled
        ? shippingAddressState.addressType === ADDRESS_TYPE.company
        : undefined,
    companyName: isShippingAddressRedesignEnabled
        ? shippingAddressState.addressType === ADDRESS_TYPE.company
            ? shippingAddressState.companyName
            : undefined
        : undefined,
    firstAddressLine: shippingAddressState.address ?? '',
    secondAddressLine: shippingAddressState.address2,
    zip: shippingAddressState.postCode ?? '',
    city: shippingAddressState.city ?? '',
    countryCode: shippingAddressState.country ?? '',
    addressee: shippingAddressState.addressee,
});

const buildAdditionalVat = (invoiceDataState: InvoiceDataSchemaType) => {
    if (invoiceDataState.invoicing.additional.length > 0) {
        return invoiceDataState.invoicing.additional.map(vat => ({
            vatNumber: vat.vat,
            countryCode: vat.country,
            permanentEstablishment: vat.permanentEstablishment === 'true',
        }));
    }
};

const mapVatList = (vatList: InvoiceDetailsSchemaType['vatList']): ContractUpdateType['vatList'] =>
    vatList.map(({ vat, country, permanentEstablishment }) => ({
        vatNumber: vat,
        countryCode: country,
        permanentEstablishment: permanentEstablishment === 'true',
    }));

export const buildContractUpdate = (
    contact: ContactDetailsSchemaType,
    invoice: InvoiceDetailsSchemaType,
    organizationNumber?: string
): ContractUpdateType => ({
    vatList: mapVatList(invoice.vatList),
    organizationNumber,
    invoiceEmailAddress: invoice.email,
    contactEmailAddress: contact.email,
    contactPhoneNumber: contact.rawPhone,
    communicationLanguage: contact.communicationLanguage,
    eInvoicingId:
        invoice.schemeId && invoice.participantIdentifier
            ? `${invoice.schemeId}:${invoice.participantIdentifier}`
            : undefined,
});

export const buildOnboardingRequest = (
    cardsState: ChargingCardsSchemaType,
    supportLabel: string,
    supportPhone: string,
    invoiceDataState: InvoiceDataSchemaType,
    shippingAddressState: ShippingSchemaType,
    termsVersion: string,
    isShippingAddressRedesignEnabled = false
): OnboardingRequestType => {
    const eInvoicingId =
        invoiceDataState.invoicing.schemeId && invoiceDataState.invoicing.participantIdentifier
            ? `${invoiceDataState.invoicing.schemeId}:${invoiceDataState.invoicing.participantIdentifier}`
            : undefined;

    const companyDetails: CompanyDetailsType = {
        organizationNumber: invoiceDataState.company.companyRegistrationNumber,
        primaryVat: invoiceDataState.invoicing.vat,
        vatList: buildAdditionalVat(invoiceDataState),
        invoiceEmailAddress: invoiceDataState.invoicing.invoiceEmailAddress,
        eInvoicingId: eInvoicingId,
    };

    const contactDetails: ContactDetailsType = {
        contactPhoneNumber: buildPhoneNumber(
            invoiceDataState.contact.countryCode,
            invoiceDataState.contact.phoneNumber
        ),
        contactEmail: invoiceDataState.contact.personalEmailAddress,
        communicationLanguage: invoiceDataState.contact.languageOfCommunication,
    };

    const cardOrderDetails: CardOrderDetailsType = {
        shippingAddress: buildShippingAddress(shippingAddressState, isShippingAddressRedesignEnabled),
        cardDetails: cardsState.cardsListCustomization.map(c => ({
            firstRow: c.line1,
            secondRow: c.line2,
            alias: '',
            productId: 95379,
            supportLabel,
            supportPhone,
        })),
    };

    return {
        companyDetails,
        contactDetails,
        cardOrderDetails,
        termsVersion,
    };
};
